import { Logout, Menu as MenuIcon, SwapHoriz, Business } from "@mui/icons-material";
import {
  Avatar,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import HeaderDrawer from "./partials/Drawer";
import useApp from "hooks/useApp";
import { useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";
import LanguageSwitcher from "components/LanguageSwitcher";
import { getUserData } from "helpers";
import axiosInstance from "helpers/axiosInstance";
import { 
  CAN_SWITCH_ROLE, 
  GET_USER_ROLES, 
  GET_USER_ENTITIES, 
  SWITCH_ROLE, 
  SWITCH_ENTITY 
} from "constants/api";
import { LocalStorage } from "enums/localStorage";

interface IRole {
  id: string;
  name: string;
  normalizedName: string;
}

interface IEntity {
  id: string;
  name: string;
  nameAr: string;
}

const Header: React.FC = () => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const { push } = useApp();
  const { user, roleName } = getUserData()
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [canSwitch, setCanSwitch] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<IRole[]>([]);
  const [userEntities, setUserEntities] = useState<IEntity[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedEntity, setSelectedEntity] = useState<string>("");
  const [showEntityDropdown, setShowEntityDropdown] = useState<boolean>(false);

  useEffect(() => {
    checkCanSwitch();
    // Don't set selected role/entity here - wait for data to be fetched
    // They will be set in fetchUserRoles() and fetchUserEntities()
  }, []);

  useEffect(() => {
    if (canSwitch) {
      fetchUserRoles();
    }
  }, [canSwitch]);

  useEffect(() => {
    // Show entity dropdown when current role is CompanyAdmin
    const isCompanyAdmin = roleName?.toUpperCase() === "COMPANYADMIN";
    setShowEntityDropdown(isCompanyAdmin);
    
    // Fetch entities if we're CompanyAdmin and can switch
    if (isCompanyAdmin && canSwitch) {
      fetchUserEntities();
    }
  }, [roleName, canSwitch]);

  const checkCanSwitch = async () => {
    try {
      const response = await axiosInstance.get(CAN_SWITCH_ROLE);
      setCanSwitch(response.data.canSwitch);
    } catch (error) {
      console.error("Error checking switch capability:", error);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const response = await axiosInstance.get(GET_USER_ROLES);
      setUserRoles(response.data);
      
      // Match current role to set the selected value in dropdown
      if (roleName && response.data) {
        const currentRole = response.data.find((r: IRole) => 
          r.normalizedName?.toUpperCase() === roleName.toUpperCase()
        );
        if (currentRole) {
         setSelectedRole(currentRole.id);
        }
      }
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  const fetchUserEntities = async () => {
    try {
      const response = await axiosInstance.get(GET_USER_ENTITIES);
      setUserEntities(response.data);
      
      const companyId = localStorage.getItem(LocalStorage.COMPANY_ID);
      if (companyId && response.data) {
        const currentEntity = response.data.find((e: IEntity) => e.id === companyId);
        if (currentEntity) {
          setSelectedEntity(currentEntity.id);
        }
      }
    } catch (error) {
      console.error("Error fetching user entities:", error);
    }
  };

  const handleRoleChange = async (roleId: string) => {
    setSelectedRole(roleId);
    
    await handleRoleSwitch(roleId);
  };

  const handleRoleSwitch = async (roleId: string) => {
    try {
      const response = await axiosInstance.post(SWITCH_ROLE, JSON.stringify(roleId), {
        headers: { 'Content-Type': 'application/json' }
      });
      
   
      localStorage.setItem(LocalStorage.ACCESS_TOKEN, response.data.token);
      localStorage.setItem(LocalStorage.REFRESH_TOKEN, response.data.refreshToken);
      localStorage.setItem(LocalStorage.ROLE_NAME, response.data.roleName);
      localStorage.setItem(LocalStorage.ROLE_ID, roleId);
      localStorage.setItem(LocalStorage.COLORS, JSON.stringify(response.data.colors));
      localStorage.setItem(LocalStorage.USER, JSON.stringify(response.data.user));
      if (response.data.companyId) {
        localStorage.setItem(LocalStorage.COMPANY_ID, response.data.companyId);
      } else {
        localStorage.removeItem(LocalStorage.COMPANY_ID);
        setSelectedEntity("");
      }
      localStorage.setItem(LocalStorage.IS_APPROVED_VENDOR, response.data.isApprovedVendor.toString());
   
      window.location.reload();
    } catch (error) {
      console.error("Error switching role:", error);
    }
  };

  const handleEntitySwitch = async (entityId: string) => {
    try {
    
      const response = await axiosInstance.post(SWITCH_ENTITY, JSON.stringify(entityId), {
        headers: { 'Content-Type': 'application/json' }
      });
      
     
      localStorage.setItem(LocalStorage.ACCESS_TOKEN, response.data.token);
      localStorage.setItem(LocalStorage.REFRESH_TOKEN, response.data.refreshToken);
      localStorage.setItem(LocalStorage.ROLE_NAME, response.data.roleName);
      localStorage.setItem(LocalStorage.COLORS, JSON.stringify(response.data.colors));
      localStorage.setItem(LocalStorage.USER, JSON.stringify(response.data.user));
      if (response.data.companyId) {
        localStorage.setItem(LocalStorage.COMPANY_ID, response.data.companyId);
      }
      localStorage.setItem(LocalStorage.IS_APPROVED_VENDOR, response.data.isApprovedVendor.toString());
      
      setSelectedEntity(entityId);
 
      window.location.reload();
    } catch (error) {
      console.error("Error switching entity:", error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    handleClose();
    localStorage.clear();
    push("/login");
    return;
  };
  return (
    <Stack
      component="header"
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{ p: 1 }}
    >
      <IconButton
        sx={{ display: { xs: "block", lg: "none" } }}
        size="small"
        onClick={() => setOpenDrawer(true)}
      >
        <MenuIcon />
      </IconButton>
      <HeaderDrawer openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} />

      <Grid container justifyContent={"flex-end"} alignItems={"center"} gap={2}>
        <Typography variant="subtitle1">{t('T_welcome')}, {user.userName}</Typography>
        <Tooltip title="Profile">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Avatar sx={{ width: 32, height: 32 }} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            sx: { minWidth: 250 }
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              push("/profile");
            }}
            style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%" }}
          >
            <PersonIcon /> {t("T_Profile")}
          </MenuItem>

          {(() => {
            return canSwitch && userRoles.length > 0;
          })() && (
            <>
              <Divider />
              <MenuItem onClick={(e) => e.stopPropagation()}>
                <FormControl fullWidth size="small">
                  <InputLabel id="role-select-label">
                    <SwapHoriz fontSize="small" style={{ marginRight: 8 }} />
                    {t("T_SwitchRole") || "Switch Role"}
                  </InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={selectedRole}
                    label={t("T_SwitchRole") || "Switch Role"}
                    onChange={(e) => handleRoleChange(e.target.value)}
                  >
                    {userRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MenuItem>
            </>
          )}

          {(() => {
           return canSwitch && showEntityDropdown && userEntities.length > 0;
          })() && (
            <>
              <Divider />
              <MenuItem onClick={(e) => e.stopPropagation()}>
                <FormControl fullWidth size="small">
                  <InputLabel id="entity-select-label">
                    <Business fontSize="small" style={{ marginRight: 8 }} />
                    {t("T_SwitchEntity") || "Switch Company"}
                  </InputLabel>
                  <Select
                    labelId="entity-select-label"
                    value={selectedEntity}
                    label={t("T_SwitchEntity") || "Switch Company"}
                    onChange={(e) => handleEntitySwitch(e.target.value)}
                  >
                    {userEntities.map((entity) => (
                      <MenuItem key={entity.id} value={entity.id}>
                        {i18n.language === 'ar' ? entity.nameAr : entity.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MenuItem>
            </>
          )}

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            {t("T_logout")}
          </MenuItem>
        </Menu>
      </Grid>
    </Stack>
  );
};

export default Header;
